/**
 * Two things about syncing with Grok Bot (DASHBOARD.md §1.16, §3.6):
 *
 *  useSyncPoll   asks the server "anything new?" every 60 s while the tab is
 *                visible (20 s while a thread is open, 5 min hidden) and, when a
 *                new message or snapshot has arrived, refreshes the page data.
 *
 *  useSyncNow    the "Sync now" button: requests a sync, then follows it through
 *                Sync requested → Grok Bot acked → New data received. Rate
 *                limited to 1 per 30 s (the countdown), and after 3 minutes
 *                without new data it says so and offers Retry.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { BotOpsHttpError } from '../api/botOpsClient';
import { getSyncStatus, requestSync } from '../api/botOpsService';

export const SYNC_COOLDOWN_S = 30;
export const SYNC_GIVE_UP_MS = 3 * 60 * 1000;
export const SYNC_TRACK_EVERY_MS = 8 * 1000;

export function useSyncPoll({ refresh, threadOpen }: { refresh: () => Promise<void> | void; threadOpen: boolean }) {
  const cursor = useRef<string | null>(null);
  const snap = useRef<string | null>(null);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const open = useRef(threadOpen);
  open.current = threadOpen;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await getSyncStatus(cursor.current);
        const d = r.data;
        if (d && !cancelled) {
          const first = cursor.current === null && snap.current === null;
          const newSnapshot = d.latest_snapshot ? d.latest_snapshot.sha256 : null;
          const changed = (!first && d.new_messages.length > 0) || (!first && newSnapshot !== null && snap.current !== null && newSnapshot !== snap.current);
          if (d.latest_message_id) cursor.current = d.latest_message_id;
          snap.current = newSnapshot || snap.current;
          if (changed) await refreshRef.current();
        }
      } catch { /* the snapshot poll shows the connection error; this one just tries again */ }
      if (cancelled) return;
      const delay = document.visibilityState !== 'visible' ? 5 * 60 * 1000 : open.current ? 20 * 1000 : 60 * 1000;
      timer = setTimeout(tick, delay);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);
}

export type SyncPhase = 'idle' | 'running' | 'received' | 'noNewer' | 'error';

export interface SyncNowState {
  phase: SyncPhase;
  /** Which steps have ticked: requested, Grok Bot acked, new data received. */
  steps: [boolean, boolean, boolean];
  /** Seconds until the button is usable again. */
  cooldown: number;
  error: string | null;
}

export function useSyncNow({ enabled, generatedIct, refresh }: { enabled: boolean; generatedIct: string | null; refresh: () => Promise<void> | void }) {
  const [state, setState] = useState<SyncNowState>({ phase: 'idle', steps: [false, false, false], cooldown: 0, error: null });
  const baseline = useRef<number>(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const runId = useRef(0);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const generatedRef = useRef(generatedIct);
  generatedRef.current = generatedIct;

  useEffect(() => () => {
    runId.current += 1;
    if (timer.current) clearTimeout(timer.current);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    let left = seconds;
    setState((s) => ({ ...s, cooldown: left }));
    cooldownTimer.current = setInterval(() => {
      left -= 1;
      setState((s) => ({ ...s, cooldown: Math.max(0, left) }));
      if (left <= 0 && cooldownTimer.current) { clearInterval(cooldownTimer.current); cooldownTimer.current = undefined; }
    }, 1000);
  }, []);

  const sync = useCallback(async () => {
    if (!enabled) return;
    const id = (runId.current += 1);
    if (timer.current) clearTimeout(timer.current);
    baseline.current = generatedRef.current ? new Date(generatedRef.current).getTime() : 0;
    setState((s) => ({ ...s, phase: 'running', steps: [false, false, false], error: null }));
    let requestId: string;
    try {
      const r = await requestSync();
      requestId = (r.data as { request_id: string }).request_id;
    } catch (e) {
      const err = e instanceof BotOpsHttpError ? e : null;
      if (err && err.status === 429) startCooldown(err.retryAfter || SYNC_COOLDOWN_S);
      setState((s) => ({ ...s, phase: 'error', error: err && err.status === 429 ? `Sync was just requested. Try again in ${err.retryAfter || SYNC_COOLDOWN_S} s.` : (err ? err.message : 'Could not request a sync') }));
      return;
    }
    if (id !== runId.current) return;
    startCooldown(SYNC_COOLDOWN_S);
    setState((s) => ({ ...s, steps: [true, false, false] }));

    const startedAt = Date.now();
    const track = async () => {
      if (id !== runId.current) return;
      try {
        const r = await getSyncStatus(null);
        const d = r.data;
        if (d && id === runId.current) {
          const mine = d.sync_requests.find((x) => x.id === requestId);
          const acked = Boolean(mine && mine.acked_at);
          const newer = d.latest_snapshot ? new Date(d.latest_snapshot.generated_ict).getTime() > baseline.current : false;
          if (newer) {
            setState((s) => ({ ...s, phase: 'received', steps: [true, true, true] }));
            await refreshRef.current();
            return;
          }
          if (acked) setState((s) => ({ ...s, steps: [true, true, false] }));
        }
      } catch { /* keep tracking: a blip must not end the tracker */ }
      if (id !== runId.current) return;
      if (Date.now() - startedAt >= SYNC_GIVE_UP_MS) {
        setState((s) => ({ ...s, phase: 'noNewer' }));
        return;
      }
      timer.current = setTimeout(track, SYNC_TRACK_EVERY_MS);
    };
    timer.current = setTimeout(track, SYNC_TRACK_EVERY_MS);
  }, [enabled, startCooldown]);

  return { ...state, sync };
}
